import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box, Button, Chip, IconButton, TextField,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import {
  Add, InfoOutlined, ModeEditOutlineTwoTone, FileDownloadOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";
import { localizeDateFull } from "utils/dateFormats";
import { exportExcelFile } from "utils/exportExcelFile";

import {
  useLazyListVehicleListingsQuery,
  useLazyExportVehicleListingsQuery,
} from "store/services/vehicleListingApi";

const VAT_BILL_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Has VAT Bill", value: "true" },
  { label: "No VAT Bill", value: "false" },
];

const VehicleListingListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const rawKeyword = searchParams.get("search") || "";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vatBillFilter, setVatBillFilter] = useState("ALL");
  // Debounced keyword — only fires API call when empty OR length >= 3
  const [debouncedKeyword, setDebouncedKeyword] = useState("");

  // ── Debounce: update debouncedKeyword 400ms after rawKeyword stops changing,
  //    but only propagate when length is 0 or >= 3
  useEffect(() => {
    const handler = setTimeout(() => {
      if (rawKeyword.length === 0 || rawKeyword.length >= 3) {
        setDebouncedKeyword(rawKeyword);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [rawKeyword]);

  const breadcrumbs = [{ title: "Vehicles", path: "/app/crm/vehicle/list" }, { title: "External Driver Record" }];
  const vehicleListingTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  // ── RTK Query ───────────────────────────────────────────────────────────────
  const [
    fetchVehicleListings,
    { data: vehicleListingRes, isLoading, isFetching, error },
  ] = useLazyListVehicleListingsQuery();

  const [exportList, { isFetching: isExporting }] =
    useLazyExportVehicleListingsQuery();

  useEffect(() => {
    fetchVehicleListings({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(debouncedKeyword && { keyword: debouncedKeyword }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(vatBillFilter !== "ALL" && { hasVATBill: vatBillFilter }),
    });
  }, [
    activeTab, currentPage, perPage,
    debouncedKeyword, startDate, endDate, vatBillFilter,
    fetchVehicleListings,
  ]);

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const res = await exportList({
      active: activeTab === "Active",
      ...(debouncedKeyword && { keyword: debouncedKeyword }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(vatBillFilter !== "ALL" && { hasVATBill: vatBillFilter }),
    });

    const rows = res?.data?.data ?? [];
    if (!rows.length) return;

    const sheetData = rows.map((row, index) => ({
      "S.N.": index + 1,
      "Registration Number": row?.vehicleRegistrationNumber ?? "N/A",
      "Vehicle Type": row?.vehicleType?.name ?? "N/A",
      "Base Location": row?.baseLocation ?? "N/A",
      "Payload": row?.payload?.name ?? "N/A",
      "Length": row?.length?.name ?? "N/A",
      "Body Type": row?.bodyType?.name ?? "N/A",
      "Drive Type": row?.driveType?.name ?? "N/A",
      "Driver Name": row?.driverName ?? "N/A",
      "Driver Number": row?.driverNumber ?? "N/A",
      "Owner Name": row?.ownerName ?? "N/A",
      "Owner Number": row?.ownerNumber ?? "N/A",
    }));

    const nameParts = ["External Driver Record", activeTab];
    if (vatBillFilter !== "ALL")
      nameParts.push(vatBillFilter === "true" ? "Has VAT Bill" : "No VAT Bill");
    if (debouncedKeyword)
      nameParts.push(`Search-${debouncedKeyword}`);
    if (startDate || endDate)
      nameParts.push(`${startDate || "N/A"} to ${endDate || "N/A"}`);
    if (!debouncedKeyword && !startDate && !endDate && vatBillFilter === "ALL")
      nameParts.push(new Date().toISOString().slice(0, 10));

    exportExcelFile({ sheetData, fileName: nameParts.join(" - ") });
  };

  // ── Clear filters ────────────────────────────────────────────────────────
  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setVatBillFilter("ALL");
  };

  const hasFilters = !!startDate || !!endDate || vatBillFilter !== "ALL";

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Driver",
      selector: (row) => row.driverName,
      cell: (row) =>
        row?.driverName ? (
          <div>
            <span className="text-sm">
              <HighlightedText text={row.driverName} highlight={debouncedKeyword} />
            </span>
            <br />
            <span className="text-sm text-gray-500 dark:text-gray-300">
              <HighlightedText text={row.driverNumber ?? ""} highlight={debouncedKeyword} />
            </span>
          </div>
        ) : (
          "N/A"
        ),
      minWidth: "140px",
    },
    {
      name: "Drive Type",
      selector: (row) => row.driveType?.name,
      cell: (row) =>
        row?.driveType?.name ? (
          <HighlightedText text={row.driveType.name} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "110px",
    },
    {
      name: "Owner",
      selector: (row) => row.ownerName,
      cell: (row) =>
        row?.ownerName ? (
          <div>
            <span className="text-sm">
              <HighlightedText text={row.ownerName} highlight={debouncedKeyword} />
            </span>
            <br />
            <span className="text-sm text-gray-500 dark:text-gray-300">
              <HighlightedText text={row.ownerNumber ?? ""} highlight={debouncedKeyword} />
            </span>
          </div>
        ) : (
          "N/A"
        ),
      minWidth: "140px",
    },
    {
      name: "Registration No.",
      selector: (row) => row.vehicleRegistrationNumber,
      cell: (row) => (
        <Link
          to={`/app/crm/vehicle-listing/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.vehicleRegistrationNumber ? (
            <HighlightedText
              text={row.vehicleRegistrationNumber}
              highlight={debouncedKeyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      minWidth: "160px",
    },
    {
      name: "Vehicle Type",
      selector: (row) => row.vehicleType?.name,
      cell: (row) =>
        row?.vehicleType?.name ? (
          <HighlightedText text={row.vehicleType.name} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "130px",
    },
    {
      name: "Base Location",
      selector: (row) => row.baseLocation,
      cell: (row) =>
        row?.baseLocation ? (
          <HighlightedText text={row.baseLocation} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "130px",
    },
    {
      name: "Payload",
      selector: (row) => row.payload?.name,
      cell: (row) =>
        row?.payload?.name ? (
          <HighlightedText text={row.payload.name} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "110px",
    },
    {
      name: "Length",
      selector: (row) => row.length?.name,
      cell: (row) =>
        row?.length?.name ? (
          <HighlightedText text={row.length.name} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "100px",
    },
    {
      name: "Body Type",
      selector: (row) => row.bodyType?.name,
      cell: (row) =>
        row?.bodyType?.name ? (
          <HighlightedText text={row.bodyType.name} highlight={debouncedKeyword} />
        ) : (
          "N/A"
        ),
      minWidth: "110px",
    },
    {
      name: "Has VAT Bill",
      cell: (row) => (
        <Chip
          label={row?.hasVATBill ? "Has VAT Bill" : "No VAT Bill"}
          size="small"
          sx={{
            backgroundColor: row?.hasVATBill ? "#E9F7EF" : "#F8F9F9",
            color: row?.hasVATBill ? "green" : "#7F8C8D",
            fontWeight: 500,
          }}
        />
      ),
      minWidth: "120px",
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) =>
        row?.createdAt ? <AppTableDateTime date={row.createdAt} /> : "N/A",
      minWidth: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle-listing/${row._id}/detail`}>
            <IconButton size="small" color="primary" aria-label="View">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/vehicle-listing/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useAppMeta(`${activeTab} External Driver Record`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={isLoading || isFetching}
      headerActions={<PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={vehicleListingTabs} activeTab={activeTab} />

        {/* ── Filters Row ────────────────────────────────────────────────── */}
        <Box
          sx={{
            px: 2, pt: 2, pb: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {/* VAT Bill filter */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>VAT Bill Status</InputLabel>
            <Select
              value={vatBillFilter}
              label="VAT Bill Status"
              onChange={(e) => setVatBillFilter(e.target.value)}
            >
              {VAT_BILL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date range */}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          {/* Keyword search — debounced at 3 chars inside useEffect above */}
          <TableFilter
            searchLabel="Search External Driver Record..."
            disabled={isLoading || isFetching}
          />

          {/* Clear */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            disabled={!hasFilters}
          >
            Clear
          </Button>

          {/* Export */}
          <LoadingButton
            variant="contained"
            size="small"
            color="success"
            loading={isExporting}
            loadingPosition="start"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
          >
            Export Excel
          </LoadingButton>
        </Box>

        <AppTable
          columns={columns}
          data={vehicleListingRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={vehicleListingRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VehicleListingListScreen;

const PageActions = () => (
  <Link to="/app/crm/vehicle-listing/create">
    <Button startIcon={<Add />} variant="contained">
      New External Driver Record
    </Button>
  </Link>
);